import { getCompanyFinancialData } from './calculation';

export type CalculateExchangeRateParams = {
  listingMarketCapitalisation: number;
  reportedMarketCapitalisation: number;
};

export type ConvertCurrencyParams = CalculateExchangeRateParams & {
  exchangeRate: number;
  fairValue: number;
};

export type ShouldConvertCurrencyParams = {
  listingCurrencyISO: string;
  reportingCurrencyISO: string;
};

export const calculateReportingToListingExchangeRate = ({
  listingMarketCapitalisation,
  reportedMarketCapitalisation,
}: CalculateExchangeRateParams): number => {
  if (listingMarketCapitalisation && reportedMarketCapitalisation) {
    return listingMarketCapitalisation / reportedMarketCapitalisation;
  }

  return 1;
};

export const calculateListingToReportingExchangeRate = ({
  listingMarketCapitalisation,
  reportedMarketCapitalisation,
}: CalculateExchangeRateParams): number => {
  if (listingMarketCapitalisation && reportedMarketCapitalisation) {
    return reportedMarketCapitalisation / listingMarketCapitalisation;
  }

  return 1;
};

export const shouldConvertCurrency = ({
  listingCurrencyISO,
  reportingCurrencyISO,
}: ShouldConvertCurrencyParams): boolean => {
  return listingCurrencyISO !== reportingCurrencyISO;
};

export const shouldCalculateExchangeRate = ({
  exchangeRate,
}: {
  exchangeRate: number;
}) => {
  return exchangeRate === 1;
};

export const convertToListingCurrency = ({
  listingMarketCapitalisation,
  reportedMarketCapitalisation,
  fairValue,
  exchangeRate,
  listingCurrencyISO,
  reportingCurrencyISO,
}: ConvertCurrencyParams & ShouldConvertCurrencyParams): number => {
  if (
    shouldConvertCurrency({ listingCurrencyISO, reportingCurrencyISO }) &&
    shouldCalculateExchangeRate({ exchangeRate }) &&
    listingMarketCapitalisation &&
    reportedMarketCapitalisation
  ) {
    const exchangeRate = calculateReportingToListingExchangeRate({
      listingMarketCapitalisation,
      reportedMarketCapitalisation,
    });
    return fairValue * exchangeRate;
  }

  return fairValue * exchangeRate;
};

export const convertToReportedCurrency = ({
  listingMarketCapitalisation,
  reportedMarketCapitalisation,
  fairValue,
  exchangeRate,
  listingCurrencyISO,
  reportingCurrencyISO,
}: ConvertCurrencyParams & ShouldConvertCurrencyParams): number => {
  if (
    shouldConvertCurrency({ listingCurrencyISO, reportingCurrencyISO }) &&
    shouldCalculateExchangeRate({ exchangeRate }) &&
    listingMarketCapitalisation &&
    reportedMarketCapitalisation
  ) {
    const exchangeRate = calculateListingToReportingExchangeRate({
      listingMarketCapitalisation,
      reportedMarketCapitalisation,
    });
    return fairValue * exchangeRate;
  }

  return fairValue * (1 / exchangeRate);
};

export const pickCurrencyConversionParamsFromCompanyFinancialData = ({
  listingMarketCapitalisation,
  reportedMarketCapitalisation,
  listingCurrencyISO,
  reportingCurrencyISO,
}: ReturnType<typeof getCompanyFinancialData>): ShouldConvertCurrencyParams &
  CalculateExchangeRateParams => ({
  listingMarketCapitalisation,
  reportedMarketCapitalisation,
  listingCurrencyISO,
  reportingCurrencyISO,
});
